import { useRef, useState } from "react";

interface VideoPlayerProps {
  poster: string;
  title: string;
  isLive: boolean;
}

export default function VideoPlayer({ poster, title, isLive }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const togglePlay = () => setPlaying((v) => !v);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-50"
    >
      <div className="relative aspect-video w-full">
        <img
          src={poster}
          alt={`${title}直播画面占位`}
          title={`${title}直播 - jrs直播`}
          className={`h-full w-full object-cover object-top transition-all duration-700 ${
            playing ? "scale-105 opacity-70" : "opacity-60"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-background-50/20" />

        {playing && (
          <>
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-primary-500/20 to-transparent" />
            <div className="pointer-events-none absolute inset-0 flex items-end justify-center gap-1 pb-24">
              {[18, 30, 22, 40, 26, 34, 20].map((h, i) => (
                <span
                  key={i}
                  className="w-1.5 rounded-full bg-primary-500/70 animate-pulse-dot"
                  style={{ height: `${h}px`, animationDelay: `${i * 0.12}s` }}
                />
              ))}
            </div>
          </>
        )}

        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          aria-label={playing ? "暂停播放" : "播放"}
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/90 text-background-50 transition-transform hover:scale-105 md:h-20 md:w-20">
            <i className={playing ? "ri-pause-fill text-3xl" : "ri-play-fill text-3xl"} />
          </span>
        </button>

        <div className="absolute left-4 top-4 flex items-center gap-2">
          {isLive && (
            <span className="flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
              <span className="h-1.5 w-1.5 rounded-full bg-background-50 animate-pulse-dot" />
              LIVE
            </span>
          )}
          <span className="rounded-full bg-background-50/70 px-3 py-1 text-xs font-semibold text-foreground-900">
            高清 1080P
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3 border-t border-background-200 bg-background-100 px-3 py-3 md:px-4">
        <button
          type="button"
          onClick={togglePlay}
          className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-200 cursor-pointer"
          aria-label={playing ? "暂停" : "播放"}
        >
          <i className={playing ? "ri-pause-line text-lg" : "ri-play-line text-lg"} />
        </button>
        <button
          type="button"
          onClick={() => setMuted((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-200 cursor-pointer"
          aria-label={muted ? "取消静音" : "静音"}
        >
          <i className={muted ? "ri-volume-mute-line text-lg" : "ri-volume-up-line text-lg"} />
        </button>

        <div className="hidden flex-1 items-center gap-3 sm:flex">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-background-300">
            <div
              className={`h-full rounded-full bg-primary-500 transition-all duration-1000 ${
                playing ? "w-2/3" : "w-1/4"
              }`}
            />
          </div>
          <span className="text-xs font-medium text-foreground-500">
            {playing ? "02:14" : "00:00"} / {isLive ? "直播中" : "60:00"}
          </span>
        </div>

        <button
          type="button"
          onClick={toggleFullscreen}
          className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-200 cursor-pointer"
          aria-label="全屏"
        >
          <i className="ri-fullscreen-line text-lg" />
        </button>
      </div>

      <p className="flex items-center gap-2 border-t border-background-200 bg-background-50 px-3 py-2.5 text-xs text-foreground-500 md:px-4">
        <i className="ri-information-line text-primary-400" />
        本区域为播放器结构演示，未接入真实直播信号，仅用于展示界面与交互。
      </p>
    </div>
  );
}